import React, { useState } from 'react';
import { StyleSheet, View, Text, ScrollView, TouchableOpacity } from 'react-native';
import { JobCard } from '../../types';
import theme from '../../theme';
import { Ionicons } from '@expo/vector-icons';

interface JobCardsTableProps {
  jobCards: JobCard[];
  onRowPress?: (jobCard: JobCard) => void;
  onEdit?: (jobCard: JobCard) => void;
  onDelete?: (jobCard: JobCard) => void;
  onMarkComplete?: (jobCard: JobCard) => void;
  onPrint?: (jobCard: JobCard) => void;
}

type SortField = 'jobNumber' | 'licensePlate' | 'requestDate' | 'jobStatus' | 'handoverDate';
type SortDirection = 'asc' | 'desc';

export const JobCardsTable = ({ jobCards, onRowPress, onEdit, onDelete, onMarkComplete, onPrint }: JobCardsTableProps) => {
  const [sortField, setSortField] = useState<SortField>('requestDate');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedJobCards = [...jobCards].sort((a, b) => {
    let aVal: any = a[sortField];
    let bVal: any = b[sortField];

    // Handle dates
    if (sortField === 'requestDate' || sortField === 'handoverDate') {
      aVal = aVal ? new Date(aVal).getTime() : 0;
      bVal = bVal ? new Date(bVal).getTime() : 0;
    }

    // Handle strings
    if (typeof aVal === 'string') {
      aVal = aVal.toLowerCase();
      bVal = bVal?.toLowerCase() || '';
    }

    if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
    if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return theme.colors.warning;
      case 'in_progress':
        return theme.colors.info;
      case 'completed':
        return theme.colors.success;
      case 'handed_over':
        return theme.colors.primary;
      default:
        return theme.colors.gray400;
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'pending':
        return 'Pending';
      case 'in_progress':
        return 'In Progress';
      case 'completed':
        return 'Completed';
      case 'handed_over':
        return 'Handed Over';
      default:
        return status;
    }
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getWorkTypes = (jobCard: JobCard) => {
    const types = [];
    if (jobCard.underMaintenance) types.push('Maintenance');
    if (jobCard.accidentRepair) types.push('Accident');
    if (jobCard.underMaintenanceAlQouz) types.push('Al Qouz');
    if (jobCard.underMaintenanceAgency) types.push('Agency');
    if (jobCard.others) types.push('Other');
    return types.length > 0 ? types.join(', ') : '-';
  };

  const renderSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return <Ionicons name="swap-vertical" size={14} color={theme.colors.gray400} />;
    }
    return (
      <Ionicons
        name={sortDirection === 'asc' ? 'chevron-up' : 'chevron-down'}
        size={14}
        color={theme.colors.primary}
      />
    );
  };

  return (
    <View style={styles.container}>
      <ScrollView horizontal showsHorizontalScrollIndicator={true}>
        <View>
          {/* Header Row */}
          <View style={styles.headerRow}>
            <TouchableOpacity
              style={[styles.headerCell, styles.colJobNumber]}
              onPress={() => handleSort('jobNumber')}
            >
              <Text style={styles.headerText}>Job #</Text>
              {renderSortIcon('jobNumber')}
            </TouchableOpacity>

            <TouchableOpacity
              style={[styles.headerCell, styles.colLicensePlate]}
              onPress={() => handleSort('licensePlate')}
            >
              <Text style={styles.headerText}>License Plate</Text>
              {renderSortIcon('licensePlate')}
            </TouchableOpacity>

            <View style={[styles.headerCell, styles.colVehicle]}>
              <Text style={styles.headerText}>Vehicle</Text>
            </View>

            <TouchableOpacity
              style={[styles.headerCell, styles.colDate]}
              onPress={() => handleSort('requestDate')}
            >
              <Text style={styles.headerText}>Request Date</Text>
              {renderSortIcon('requestDate')}
            </TouchableOpacity>

            <View style={[styles.headerCell, styles.colDate]}>
              <Text style={styles.headerText}>Return Date</Text>
            </View>

            <TouchableOpacity
              style={[styles.headerCell, styles.colStatus]}
              onPress={() => handleSort('jobStatus')}
            >
              <Text style={styles.headerText}>Status</Text>
              {renderSortIcon('jobStatus')}
            </TouchableOpacity>

            <View style={[styles.headerCell, styles.colWorkType]}>
              <Text style={styles.headerText}>Work Type</Text>
            </View>

            <View style={[styles.headerCell, styles.colNotes]}>
              <Text style={styles.headerText}>Notes</Text>
            </View>

            <View style={[styles.headerCell, styles.colActions]}>
              <Text style={styles.headerText}>Actions</Text>
            </View>
          </View>

          {/* Data Rows */}
          <ScrollView style={styles.tableBody}>
            {sortedJobCards.map((jobCard, index) => (
              <TouchableOpacity
                key={jobCard.id}
                style={[styles.dataRow, index % 2 === 0 && styles.dataRowEven]}
                onPress={() => onRowPress?.(jobCard)}
                activeOpacity={0.7}
              >
                <View style={[styles.cell, styles.colJobNumber]}>
                  <Text style={styles.cellText} numberOfLines={1}>
                    {jobCard.jobNumber}
                  </Text>
                </View>

                <View style={[styles.cell, styles.colLicensePlate]}>
                  <Text style={[styles.cellText, styles.cellTextBold]} numberOfLines={1}>
                    {jobCard.licensePlate}
                  </Text>
                </View>

                <View style={[styles.cell, styles.colVehicle]}>
                  <Text style={styles.cellText} numberOfLines={2}>
                    {jobCard.makeModel || '-'}
                  </Text>
                </View>

                <View style={[styles.cell, styles.colDate]}>
                  <Text style={styles.cellText}>
                    {formatDate(jobCard.requestDate)}
                  </Text>
                </View>

                <View style={[styles.cell, styles.colDate]}>
                  <Text style={styles.cellText}>
                    {formatDate(jobCard.returnDate)}
                  </Text>
                </View>

                <View style={[styles.cell, styles.colStatus]}>
                  <View
                    style={[
                      styles.statusBadge,
                      { backgroundColor: getStatusColor(jobCard.jobStatus) + '20' },
                    ]}
                  >
                    <Text
                      style={[
                        styles.statusText,
                        { color: getStatusColor(jobCard.jobStatus) },
                      ]}
                    >
                      {getStatusLabel(jobCard.jobStatus)}
                    </Text>
                  </View>
                </View>

                <View style={[styles.cell, styles.colWorkType]}>
                  <Text style={styles.cellText} numberOfLines={2}>
                    {getWorkTypes(jobCard)}
                  </Text>
                </View>

                <View style={[styles.cell, styles.colNotes]}>
                  <Text style={styles.cellText} numberOfLines={2}>
                    {jobCard.notes || jobCard.workDescription || jobCard.conditionNotes || jobCard.jobDescription || '-'}
                  </Text>
                </View>

                <View style={[styles.cell, styles.colActions]}>
                  {/* Edit Button */}
                  <TouchableOpacity
                    style={styles.actionButton}
                    onPress={(e) => {
                      e.stopPropagation();
                      onEdit?.(jobCard);
                    }}
                  >
                    <Ionicons name="create-outline" size={18} color={theme.colors.primary} />
                  </TouchableOpacity>

                  {/* Mark Complete Button */}
                  {(jobCard.jobStatus === 'pending' || jobCard.jobStatus === 'in_progress') && (
                    <TouchableOpacity
                      style={styles.actionButton}
                      onPress={(e) => {
                        e.stopPropagation();
                        onMarkComplete?.(jobCard);
                      }}
                    >
                      <Ionicons name="checkmark-done" size={18} color={theme.colors.success} />
                    </TouchableOpacity>
                  )}

                  {/* Print Button */}
                  <TouchableOpacity
                    style={styles.actionButton}
                    onPress={(e) => {
                      e.stopPropagation();
                      onPrint?.(jobCard);
                    }}
                  >
                    <Ionicons name="print" size={18} color={theme.colors.info} />
                  </TouchableOpacity>

                  {/* Delete Button */}
                  <TouchableOpacity
                    style={styles.actionButton}
                    onPress={(e) => {
                      e.stopPropagation();
                      onDelete?.(jobCard);
                    }}
                  >
                    <Ionicons name="trash" size={18} color={theme.colors.error} />
                  </TouchableOpacity>
                </View>
              </TouchableOpacity>
            ))}
          </ScrollView>
        </View>
      </ScrollView>

      {/* Total Count */}
      <View style={styles.footer}>
        <Text style={styles.footerText}>
          Total: {sortedJobCards.length} job{sortedJobCards.length !== 1 ? 's' : ''}
        </Text>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.white,
    borderRadius: theme.borderRadius.lg,
    marginHorizontal: theme.spacing.md,
    marginBottom: theme.spacing.md,
    ...theme.shadows.md,
  },
  headerRow: {
    flexDirection: 'row',
    backgroundColor: theme.colors.gray100,
    borderBottomWidth: 2,
    borderBottomColor: theme.colors.gray300,
  },
  headerCell: {
    paddingVertical: theme.spacing.sm,
    paddingHorizontal: theme.spacing.xs,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 4,
  },
  headerText: {
    fontSize: theme.typography.sizes.sm,
    fontWeight: '700' as const,
    color: theme.colors.textPrimary,
  },
  tableBody: {
    maxHeight: 600,
  },
  dataRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.gray200,
    backgroundColor: theme.colors.white,
  },
  dataRowEven: {
    backgroundColor: theme.colors.gray50,
  },
  cell: {
    paddingVertical: theme.spacing.sm,
    paddingHorizontal: theme.spacing.xs,
    justifyContent: 'center',
  },
  cellText: {
    fontSize: theme.typography.sizes.sm,
    color: theme.colors.textPrimary,
  },
  cellTextBold: {
    fontWeight: '600' as const,
  },
  statusBadge: {
    paddingHorizontal: theme.spacing.xs,
    paddingVertical: 4,
    borderRadius: theme.borderRadius.sm,
    alignSelf: 'flex-start',
  },
  statusText: {
    fontSize: theme.typography.sizes.xs,
    fontWeight: '600' as const,
  },
  actionButton: {
    padding: theme.spacing.xs,
    marginHorizontal: 2,
  },
  colActions: {
    width: 160,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  footer: {
    borderTopWidth: 1,
    borderTopColor: theme.colors.gray300,
    paddingVertical: theme.spacing.sm,
    paddingHorizontal: theme.spacing.md,
    backgroundColor: theme.colors.gray50,
  },
  footerText: {
    fontSize: theme.typography.sizes.sm,
    fontWeight: '600' as const,
    color: theme.colors.textSecondary,
  },
  // Column widths
  colJobNumber: {
    width: 150,
  },
  colLicensePlate: {
    width: 120,
  },
  colVehicle: {
    width: 180,
  },
  colDate: {
    width: 120,
  },
  colStatus: {
    width: 120,
  },
  colWorkType: {
    width: 150,
  },
  colNotes: {
    width: 200,
  },
});
